import type { VisitorState, VisitorTier } from "./types";

const STORAGE_KEY = "rc:visitor";
const SESSION_KEY = "rc:session";

function uuid(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* noop */
  }
  return "v-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

function safeGet(): VisitorState | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as VisitorState;
  } catch {
    return null;
  }
}

function safeSet(v: VisitorState): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* noop */
  }
}

export function loadOrInitVisitor(): VisitorState {
  if (typeof window === "undefined") {
    return {
      id: "ssr",
      visitCount: 1,
      firstAt: 0,
      lastAt: 0,
      maxScrollDepth: 0,
      totalTimeMs: 0,
      converted: false,
    };
  }

  const now = Date.now();
  let visitor = safeGet();

  let isNewSession = false;
  try {
    const sessionStart = sessionStorage.getItem(SESSION_KEY);
    if (!sessionStart) {
      sessionStorage.setItem(SESSION_KEY, String(now));
      isNewSession = true;
    }
  } catch {
    /* noop */
  }

  if (!visitor) {
    visitor = {
      id: uuid(),
      visitCount: 1,
      firstAt: now,
      lastAt: now,
      maxScrollDepth: 0,
      totalTimeMs: 0,
      converted: false,
    };
  } else if (isNewSession) {
    visitor = { ...visitor, visitCount: visitor.visitCount + 1, lastAt: now };
  }

  safeSet(visitor);
  return visitor;
}

export function updateScrollDepth(depth: number): void {
  const v = safeGet();
  if (!v) return;
  if (depth > v.maxScrollDepth) {
    safeSet({ ...v, maxScrollDepth: depth });
  }
}

export function addTimeOnSite(ms: number): void {
  const v = safeGet();
  if (!v) return;
  safeSet({ ...v, totalTimeMs: v.totalTimeMs + ms });
}

export function markConverted(): void {
  const v = safeGet();
  if (!v) return;
  safeSet({ ...v, converted: true });
}

export function computeTier(v: VisitorState): VisitorTier {
  if (v.visitCount <= 1) return "new";
  const engaged = v.maxScrollDepth >= 0.6 || v.totalTimeMs >= 60_000;
  if (engaged && !v.converted) return "high-intent";
  return "returning";
}
